import type { Comment } from '@/@types/comment'
import { AttachmentsSchema, FormAttachmentSchema } from '@/components/Attachment/schema'
import { AttachmentType } from '@/utils/fetchers/attachment'
import { compact, isEmpty } from 'lodash-es'
import { z } from 'zod'
import { filterPicAttachments, toAttachments } from '../Attachment/utils'

export interface ReplyToComment {
	to: Comment
	// 如果 threadId 存在，则表示要回复的不是一级评论
	// 一级评论是没有 threadId 的
	threadId?: string
}

export const CommentFormSchema = z.object({
	// 帖子内容验证
	content: z.string(),

	attachment: FormAttachmentSchema,

	isSyncToPersonalUpdate: z.boolean(),
})

export type CommentFormValue = z.infer<typeof CommentFormSchema>

export const InPayloadAttachmentsSchema = AttachmentsSchema.superRefine((attachments, ctx) => {
	const hasPictures = attachments.some((item) => item.type === AttachmentType.PICTURE)
	const hasVideo = attachments.some((item) => item.type === AttachmentType.VIDEO)
	const hasLink = attachments.some((item) => item.type === AttachmentType.LINK)

	if (hasVideo || hasLink) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			message: '不支持添加视频或链接',
		})
	}

	if (hasPictures && attachments.length > 1) {
		ctx.addIssue({
			code: z.ZodIssueCode.custom,
			message: '只能添加1张图片',
		})
	}
})

export const CreateCommentPayloadSchema = z
	.object({
		targetType: z.string(),
		targetId: z.string(),
		threadId: z.string().optional(),
		replyToCommentId: z.string().optional(),
	})
	.merge(
		CommentFormSchema.pick({
			content: true,
			attachment: true,
			isSyncToPersonalUpdate: true,
		})
	)
	.transform(
		async (
			{
				content,
				attachment,
				isSyncToPersonalUpdate,
				targetType,
				targetId,
				threadId,
				replyToCommentId,
			},
			ctx
		) => {
			const attachments = await InPayloadAttachmentsSchema.parseAsync(toAttachments(attachment))
			const pictureKeys = compact(
				filterPicAttachments(attachments).map((item) => item.cdn?.file.key)
			)

			if (isEmpty(content) && isEmpty(pictureKeys)) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: '请输入内容',
				})
				return
			}

			return {
				targetType,
				targetId,
				content,
				syncToPersonalUpdates: isSyncToPersonalUpdate,
				pictureKeys,
				force: false,
				threadId,
				replyToCommentId,
			}
		}
	)
